import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getParentChildren, getChildSubjects } from "../../redux/slices/lessonsSlice";
import { getChildReports } from "../../redux/slices/studentReportsSlice";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination, Autoplay } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import styles from "./ParentChildReports.module.css";
import { useDarkMode } from "../../contexts/DarkModeContext";

function ParentChildReports() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { isDarkMode } = useDarkMode();
    
    const { 
        parentChildren,
        parentChildrenLoading,
        childSubjects,
        childSubjectsLoading
    } = useSelector((state) => state.lessons);
    
    const { 
        childReports,
        childReportsLoading
    } = useSelector((state) => state.studentReports);
    
    const { currentUser } = useSelector((state) => state.auth);
    
    const [selectedChild, setSelectedChild] = useState(null);
    const [selectedSubjectId, setSelectedSubjectId] = useState(0); // 0 = الكل افتراضياً
    const [currentPage, setCurrentPage] = useState(1);
    const [isGuestMode, setIsGuestMode] = useState(true);

    const userRole = currentUser?.role || localStorage.getItem("userRole");
    const isParent = userRole === "parent";

    // التحقق من وجود token عند تحميل المكون
    useEffect(() => {
        const token = localStorage.getItem("token3almny");
        if (token) {
            setIsGuestMode(false);
            if (isParent) {
                dispatch(getParentChildren());
            }
        } else {
            setIsGuestMode(true);
        }
    }, [dispatch, isParent]);

    // جلب مواد الابن عند تغيير الابن المختار
    useEffect(() => {
        if (selectedChild) {
            console.log("جلب مواد الابن:", selectedChild.id);
            dispatch(getChildSubjects(selectedChild.id));
        }
    }, [selectedChild, dispatch]);

    // جلب تقارير الابن عند تغيير الابن أو المادة
    useEffect(() => {
        if (selectedChild) {
            console.log("جلب تقارير الابن:", selectedChild.id, "المادة:", selectedSubjectId);
            dispatch(getChildReports({ 
                childId: selectedChild.id, 
                subjectId: selectedSubjectId,
                page: currentPage,
                perPage: 900
            }));
        }
    }, [selectedChild, selectedSubjectId, currentPage, dispatch]);

    // تحديث selectedChild عند تغيير parentChildren
    useEffect(() => {
        if (parentChildren.length > 0 && !selectedChild) {
            const firstChild = parentChildren[0];
            setSelectedChild(firstChild);
            // جلب مواد وتقارير الابن الأول مباشرة
            if (firstChild) {
                console.log("تعيين الابن الأول وجلب مواده وتقاريره:", firstChild.id);
                dispatch(getChildSubjects(firstChild.id));
                dispatch(getChildReports({ 
                    childId: firstChild.id, 
                    subjectId: 0,
                    page: 1,
                    perPage: 900
                }));
            }
        }
    }, [parentChildren, selectedChild, dispatch]);

    // دالة تغيير المادة
    const handleSubjectChange = (subjectId) => {
        console.log("تغيير المادة إلى:", subjectId);
        setSelectedSubjectId(subjectId);
        setCurrentPage(1);
    };

    // دالة عرض تفاصيل التقرير
    const handleViewReport = (reportId) => {
        navigate(`/report-details/${reportId}`);
    };

    // تنسيق البيانات للعرض
    const formatReports = (reports) => {
        if (!reports || !Array.isArray(reports)) return [];
        
        return reports.map(report => ({
            id: report.id,
            reportName: report.report_name || "تقرير",
            subjectName: report.subject_name || "غير محدد"
        }));
    };

    // تحديد البيانات التي سيتم عرضها
    let tabsData = [];
    let reportsToShow = [];

    if (!isGuestMode && isParent) {
        tabsData = childSubjects?.data || [];
        reportsToShow = formatReports(childReports?.data || []);
    }

    // Debug: طباعة البيانات
    useEffect(() => {
        console.log("=== DEBUG ===");
        console.log("parentChildren:", parentChildren);
        console.log("selectedChild:", selectedChild);
        console.log("childSubjects:", childSubjects);
        console.log("childSubjectsLoading:", childSubjectsLoading);
        console.log("childReports:", childReports);
        console.log("childReportsLoading:", childReportsLoading);
        console.log("reportsToShow:", reportsToShow);
        console.log("=== END DEBUG ===");
    }, [parentChildren, selectedChild, childSubjects, childReports, childReportsLoading, childSubjectsLoading, reportsToShow]);

    if (!isParent || isGuestMode) {
        return (
            <div className={styles.accessDenied}>
                <h2>غير مسموح بالوصول لهذه الصفحة</h2>
            </div>
        );
    }

    return (
        <section className={`${styles.parentChildReportsPage} ${isDarkMode ? styles.darkMode : styles.lightMode}`}>
            <div className={styles.animatedBackground}>
                <div className={styles.floatingElements}>
                    <div className={styles.floatingElement}>📊</div>
                    <div className={styles.floatingElement}>📈</div>
                    <div className={styles.floatingElement}>📋</div>
                    <div className={styles.floatingElement}>📝</div>
                    <div className={styles.floatingElement}>📚</div>
                    <div className={styles.floatingElement}>🎯</div>
                </div>
            </div>
            
            <div className="container">
                <div className={styles.parentChildReports}>
                    <div className={styles.reportsBreadcrumb}>
                        <h1 className={styles.breadcrumbTitle}>تقارير الأبناء</h1>
                        <div className={styles.breadcrumbLinks}>
                            <span className={styles.breadcrumbLink}>الرئيسية</span>
                            <span className={styles.breadcrumbSeparator}>/</span>
                            <span className={styles.breadcrumbCurrent}>تقارير الأبناء</span>
                        </div>
                    </div>

                    {/* Dropdown لاختيار الابن */}
                    <div className={styles.childSelector}>
                        <label htmlFor="childSelect" className={styles.formLabel}>
                            اختر الابن:
                        </label>
                        <select
                            id="childSelect"
                            className={styles.formSelect}
                            value={selectedChild?.id || ""}
                            onChange={(e) => {
                                console.log("تم تغيير الابن إلى:", e.target.value);
                                const child = parentChildren.find(c => c.id == e.target.value);
                                console.log("الابن المختار:", child);
                                setSelectedChild(child);
                                // إعادة تعيين المادة إلى الكل عند تغيير الابن
                                setSelectedSubjectId(0);
                            }}
                            disabled={parentChildrenLoading}
                        >
                            {parentChildrenLoading ? (
                                <option>جاري التحميل...</option>
                            ) : parentChildren.length > 0 ? (
                                parentChildren.map(child => (
                                    <option key={child.id} value={child.id}>
                                        {child.name}
                                    </option>
                                ))
                            ) : (
                                <option>لا يوجد أبناء</option>
                            )}
                        </select>
                    </div>

                    {/* Tabs المواد */}
                    {selectedChild && (
                        <div className={styles.subjectsTabs}>
                            {console.log("🔍 عرض المواد - childSubjects:", childSubjects)}
                            {console.log("🔍 عرض المواد - childSubjects.data:", childSubjects?.data)}
                            {console.log("🔍 عرض المواد - selectedChild:", selectedChild)}
                            
                            {/* خيار الكل - يظهر دائماً */}
                            <button
                                className={`${styles.subjectTab} ${selectedSubjectId === 0 ? styles.active : ''}`}
                                onClick={() => handleSubjectChange(0)}
                            >
                                <img 
                                    src="/science.png" 
                                    alt="الكل" 
                                    className={styles.tabIcon} 
                                />
                                <span>الكل</span>
                            </button>
                            
                            {/* باقي المواد - تظهر فقط إذا وجدت */}
                            {childSubjects?.data && childSubjects.data.length > 0 && 
                                childSubjects.data.map((subject) => (
                                    <button
                                        key={subject.id}
                                        className={`${styles.subjectTab} ${selectedSubjectId === subject.id ? styles.active : ''}`}
                                        onClick={() => handleSubjectChange(subject.id)}
                                    >
                                        <img 
                                            src="/science.png" 
                                            alt={subject.title} 
                                            className={styles.tabIcon} 
                                        />
                                        <span>{subject.title}</span>
                                    </button>
                                ))
                            }
                        </div>
                    )}

                    {/* عرض التقارير */}
                    <div className={styles.reportsSection}>
                        {childReportsLoading ? (
                            <div className={styles.loadingSpinner}>
                                <div className={styles.spinner}></div>
                                <p>جاري تحميل التقارير...</p>
                            </div>
                        ) : reportsToShow.length > 0 ? (
                                                    <div className={styles.reportsGrid}>
                            {reportsToShow.map((report) => (
                                <div key={report.id} className={styles.reportCard}>
                                    <div className={styles.reportHeader}>
                                        <h3 className={styles.reportTitle}>{report.reportName}</h3>
                                        <span className={styles.reportSubject}>{report.subjectName}</span>
                                    </div>
                                    
                                    <div className={styles.reportActions}>
                                        <button 
                                            className={styles.viewReportBtn}
                                            onClick={() => handleViewReport(report.id)}
                                        >
                                            عرض التقرير
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                        ) : (
                            <div className={styles.noReports}>
                                <p>لا توجد تقارير متاحة</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
}

export default ParentChildReports;
